import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { HlmIconComponent, HlmInputDirective } from '../../ui';
import { SearchService } from '../../features/search/search.service';
import { SearchEntityFilter, SearchFieldFilter, SearchResult } from '../../features/search/search.model';

/**
 * Reusable typeahead input for ID-style fields backed by the global Elasticsearch index.
 * Implements `ControlValueAccessor` so it slots straight into reactive form bindings via
 * `formControlName`. The control value is always the entity ID (string), but the visible
 * text shows ID + a human-readable label (company / full name) once a hit is selected.
 */
@Component({
  selector: 'cmt-id-autocomplete',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmIconComponent, HlmInputDirective],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IdAutocompleteComponent), multi: true },
  ],
  template: `
    <div class="group relative">
      <input
        #inputEl
        hlmInput
        type="text"
        autocomplete="off"
        spellcheck="false"
        [attr.id]="inputId()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [value]="displayValue()"
        (input)="onTextInput($event)"
        (focus)="onFocus()"
        (keydown)="onKeydown($event)"
        class="w-full pr-9"
        role="combobox"
        aria-haspopup="listbox"
        [attr.aria-expanded]="dropdownOpen()"
        [attr.aria-activedescendant]="activeDescendantId()"
      />
      <!-- Right-edge indicator: clear button (when value present) replaces the status icon on hover. -->
      @if (loading()) {
        <div class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          <hlm-icon name="lucideRefreshCw" size="0.85rem" class="animate-spin" />
        </div>
      } @else if (query() || selectedHit()) {
        <button
          type="button"
          (click)="clear()"
          [disabled]="disabled()"
          class="absolute right-2 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring/40"
          [attr.aria-label]="'Clear ' + (placeholder() || 'value')"
        >
          <hlm-icon name="lucideTrash2" size="0.85rem" />
        </button>
      } @else {
        <div class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          <hlm-icon name="lucideSearch" size="0.85rem" />
        </div>
      }

      @if (dropdownOpen() && (hits().length || query().length >= 2)) {
        <div
          class="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg"
          role="listbox"
        >
          @if (hits().length) {
            <ul class="max-h-72 overflow-y-auto py-1">
              @for (h of hits(); track h.entityId; let i = $index) {
                <li
                  [id]="optionId(i)"
                  role="option"
                  [attr.aria-selected]="i === activeIndex()"
                  class="cmt-typeahead-option flex cursor-pointer items-baseline gap-2 px-3 py-1.5 text-sm"
                  [class.cmt-typeahead-option-active]="i === activeIndex()"
                  (mousedown)="$event.preventDefault()"
                  (click)="selectHit(h)"
                  (mouseenter)="activeIndex.set(i)"
                >
                  <span class="font-mono-feat shrink-0 text-foreground">{{ h.entityId }}</span>
                  <span class="shrink-0 text-muted-foreground">·</span>
                  <span class="min-w-0 flex-1 truncate">
                    <span class="text-foreground">{{ labelForHit(h) }}</span>
                    @if (h.accent) {
                      <span class="text-foreground">&nbsp;{{ h.accent }}</span>
                    }
                    @if (h.subline) {
                      <span class="text-muted-foreground">&nbsp;· {{ h.subline }}</span>
                    }
                  </span>
                  @if (h.brand) {
                    <span class="font-mono-feat shrink-0 text-xs uppercase tracking-[0.10em] text-muted-foreground">
                      {{ h.brand }}
                    </span>
                  }
                </li>
              }
            </ul>
          } @else {
            <div class="px-3 py-2 text-sm text-muted-foreground">No matches.</div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .cmt-typeahead-option:hover,
    .cmt-typeahead-option-active {
      background-color: color-mix(in oklch, var(--primary) 12%, transparent);
    }
  `],
})
export class IdAutocompleteComponent implements ControlValueAccessor {
  /**
   * Optional custom search function — when provided, it overrides the built-in Elasticsearch
   * call (Customer/UA path). Used for the Employee field which queries `/employees?search=…`
   * directly. The function receives the trimmed query and must return mapped SearchResult-
   * shaped hits so the rendering / chip / labelForHit logic stays uniform.
   */
  readonly customSearchFn = input<((q: string) => Observable<SearchResult[]>) | null>(null);
  /** Entity type(s) for the built-in ES path (ignored when customSearchFn is provided). */
  readonly entityType = input<SearchEntityFilter | null>(null);
  /** Multiple entity types — overrides `entityType` when provided. */
  readonly entityTypes = input<SearchEntityFilter[] | null>(null);
  /** ID field used to bias the ES query when the user types digits (built-in ES path only). */
  readonly idFieldFilter = input<SearchFieldFilter | null>(null);
  /** Multiple ID fields — overrides `idFieldFilter` when provided. */
  readonly idFieldFilters = input<SearchFieldFilter[] | null>(null);
  /** Minimum trimmed character count before a query is fired. Defaults to 2. */
  readonly minChars = input<number>(2);
  /** Placeholder text for the input. */
  readonly placeholder = input<string>('');
  /** Optional input id for `<label for="…">` association. */
  readonly inputId = input<string | null>(null);
  /**
   * Optional label that the parent supplies for the value already in the form control.
   * Used to render "<id> · <label>" in the input when the component is re-created (e.g. after
   * the filter card is re-expanded) and the internally-tracked selectedHit signal is gone.
   * The display falls through to the raw query if no resolved label is available.
   */
  readonly resolvedLabel = input<string | null>(null);
  /** Disabled flag — separate from the CVA-driven `setDisabledState` path. The
   *  property name is intentionally `disabledInput` to avoid colliding with the
   *  internal computed `disabled` (combines both sources). Bind via `[disabledInput]`. */
  readonly disabledInput = input<boolean>(false);
  /** Emits the full SearchResult on selection so the parent can cross-reference (e.g. UA→customerId). */
  readonly hitSelected = output<SearchResult | null>();

  private readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl');
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchService = inject(SearchService);
  private readonly host = inject(ElementRef);

  /** CVA-driven disabled state — combined with the `disabled` input via `disabled()`. */
  private readonly cvaDisabled = signal(false);
  readonly disabled = computed(() => this.cvaDisabled() || this.disabledInput());
  readonly loading = signal(false);
  readonly query = signal('');
  readonly hits = signal<SearchResult[]>([]);
  readonly activeIndex = signal(0);
  readonly dropdownOpen = signal(false);
  readonly selectedHit = signal<SearchResult | null>(null);

  /**
   * Display string shown in the input. Priority:
   *   1. Internally-selected hit (just clicked from dropdown) → "<id> · <label>"
   *   2. Parent-supplied resolvedLabel matching the current value → "<id> · <label>"
   *      (used when the component is re-created and selectedHit is empty)
   *   3. Raw query the user is typing
   */
  readonly displayValue = computed(() => {
    const sel = this.selectedHit();
    if (sel) return `${sel.entityId} · ${this.labelForHit(sel)}`;
    const q = this.query();
    const lbl = this.resolvedLabel();
    if (q && lbl) return `${q} · ${lbl}`;
    return q;
  });

  /** Aria id of the active descendant when dropdown open. */
  readonly activeDescendantId = computed(() => {
    if (!this.dropdownOpen() || !this.hits().length) return null;
    return this.optionId(this.activeIndex());
  });

  private readonly queryStream = new Subject<string>();

  // --- ControlValueAccessor wiring ----------------------------------------
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: string | null | undefined): void {
    const next = value ?? '';
    this.query.set(next);
    if (!next) this.selectedHit.set(null);
  }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.cvaDisabled.set(isDisabled); }
  // ------------------------------------------------------------------------

  constructor() {
    this.queryStream
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((q) => {
          const trimmed = q.trim();
          if (trimmed.length < this.minChars()) {
            this.loading.set(false);
            return [[] as SearchResult[]];
          }
          this.loading.set(true);

          // Custom path (Employee): parent supplies its own observable factory.
          const custom = this.customSearchFn();
          if (custom) return custom(trimmed);

          // Built-in path (Customer / UA / PS): Elasticsearch via stateless quickSearch.
          const idFields = this.resolveIdFields();
          const entities = this.resolveEntityTypes();
          if (!idFields.length || !entities.length) {
            this.loading.set(false);
            return [[] as SearchResult[]];
          }
          // If the input is purely numeric, bias the ES query to the ID fields.
          const isNumeric = /^\d+$/.test(trimmed);
          const fieldFilters: SearchFieldFilter[] = isNumeric
            ? idFields
            : [...idFields, 'name', 'companyName'];
          // quickSearch is the stateless variant — does NOT touch the global search's
          // BehaviorSubjects, so the landing-page filter chips stay intact.
          return this.searchService.quickSearch(trimmed, {
            entityFilters: entities,
            fieldFilters,
            autoWildcard: true,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (results) => {
          this.loading.set(false);
          // Defensive type-filter only on the built-in ES path; custom paths trust the parent.
          const entities = this.resolveEntityTypes();
          const filtered = ((this.customSearchFn() || !entities.length)
            ? (results as SearchResult[])
            : (results as SearchResult[]).filter((r) =>
                entities.includes(r.entityType as SearchEntityFilter),
              )
          ).slice(0, 10);
          this.hits.set(filtered);
          this.activeIndex.set(0);
        },
        error: () => {
          this.loading.set(false);
          this.hits.set([]);
        },
      });
  }

  /** Click outside the host closes the dropdown. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.dropdownOpen.set(false);
    }
  }

  onFocus(): void {
    if (this.hits().length) this.dropdownOpen.set(true);
  }

  onTextInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    const hadSelection = this.selectedHit() != null;
    this.query.set(v);
    this.selectedHit.set(null);
    this.dropdownOpen.set(true);
    this.queryStream.next(v);
    // Propagate the raw query as the form value while the user types — once they
    // pick a hit, `selectHit` overrides this with the canonical entityId.
    this.onChange(v);
    // Notify parent that the previously-selected hit is no longer current.
    if (hadSelection) this.hitSelected.emit(null);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.hits().length) return;
      this.dropdownOpen.set(true);
      this.activeIndex.update((i) => Math.min(i + 1, this.hits().length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex.update((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      const hit = this.hits()[this.activeIndex()];
      if (hit) {
        event.preventDefault();
        this.selectHit(hit);
      }
    } else if (event.key === 'Escape') {
      this.dropdownOpen.set(false);
    }
  }

  selectHit(hit: SearchResult): void {
    this.selectedHit.set(hit);
    this.query.set(hit.entityId);
    this.dropdownOpen.set(false);
    this.onChange(hit.entityId);
    this.onTouched();
    this.hitSelected.emit(hit);
  }

  /** Reset the input value, clear any selected hit, close the dropdown, and notify the parent form. */
  clear(): void {
    const hadSelection = this.selectedHit() != null;
    this.query.set('');
    this.selectedHit.set(null);
    this.hits.set([]);
    this.dropdownOpen.set(false);
    this.onChange('');
    this.onTouched();
    if (hadSelection) this.hitSelected.emit(null);
    this.inputEl()?.nativeElement.focus();
  }

  optionId(index: number): string {
    return `${this.inputId() ?? 'cmt-typeahead'}-opt-${index}`;
  }

  /** Normalize the singular `entityType` / plural `entityTypes` inputs into a single array. */
  private resolveEntityTypes(): SearchEntityFilter[] {
    const multi = this.entityTypes();
    if (multi && multi.length) return multi;
    const single = this.entityType();
    return single ? [single] : [];
  }

  /** Same normalization for `idFieldFilter` / `idFieldFilters`. */
  private resolveIdFields(): SearchFieldFilter[] {
    const multi = this.idFieldFilters();
    if (multi && multi.length) return multi;
    const single = this.idFieldFilter();
    return single ? [single] : [];
  }

  /** Build the human label shown alongside the ID — entity-specific. */
  labelForHit(h: SearchResult): string {
    if (h.entityType === 'customer') {
      return h.companyName || `${h.firstName} ${h.lastName}`.trim() || h.detail || '—';
    }
    if (h.entityType === 'useraccount') {
      return h.detail || `${h.firstName} ${h.lastName}`.trim() || h.companyName || '—';
    }
    if (h.entityType === 'phoneservice') {
      // PS docs surface the description as both detail + companyName.
      return h.detail || h.companyName || '—';
    }
    if (h.entityType === 'employee') {
      const name = `${h.firstName} ${h.lastName}`.trim();
      return name || h.detail || h.companyName || '—';
    }
    return h.detail || h.companyName || '—';
  }
}
